import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useOutsideWork } from "@/lib/outside-work-store";
import { usePeople } from "@/lib/people-store";
import { compileOutsideBooks, type OutsideBook } from "@/lib/workshop-outside-books";
import { BookCard, type BookCardData } from "@/components/workshop/book-card";
import { formatWeight } from "@/lib/gold";
import { ArrowLeft, BookOpen, Truck } from "lucide-react";

function outsideCardData(b: OutsideBook): BookCardData {
  const sent = b.purityBooks.reduce((s, pb) => s + pb.issuedFineMg, 0);
  const received = b.purityBooks.reduce((s, pb) => s + pb.returnedFineMg, 0);
  const balance = b.purityBooks.reduce((s, pb) => s + pb.currentBalanceMg, 0);
  return {
    id: b.party.id,
    title: b.party.fullName,
    subtitle: "Outside Worker",
    personId: "type" in b.party ? b.party.id : undefined,
    icon: Truck,
    metrics: {
      receivedMg: received,
      issuedMg: sent,
      outstandingMg: balance,
      balanceMg: balance,
      lastTs: b.lastActivityTs,
      statusLabel: balance > 0 ? `${formatWeight(balance)} with worker` : "Settled",
      statusTone: balance > 0 ? "held" : "settled",
    },
  };
}

export const Route = createFileRoute("/workshop/outside-worker-books")({
  head: () => ({ meta: [{ title: "Outside Worker Books · Manufacturing Books · AVS Gold ERP" }] }),
  component: OutsideWorkerBooksPage,
});

function OutsideWorkerBooksPage() {
  const txns = useOutsideWork((s) => s.transactions);
  const people = usePeople((s) => s.people);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const books = useMemo(
    () => compileOutsideBooks(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [txns, people],
  );

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/workshop"
          className="inline-flex items-center text-sm font-semibold text-muted-foreground hover:text-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Workshop
        </Link>
        <Link
          to="/workshop/outside-work"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold hover:underline"
        >
          <Truck className="h-4 w-4" /> Open Outside Work (enter transactions)
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="font-serif text-3xl text-gold flex items-center gap-2">
          <BookOpen className="h-7 w-7" /> Outside Worker Books
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Read-only per-purity ledgers of gold sent to and returned by outside workers. Balances
          stay in their own purity. Enter transactions in the Outside Work module.
        </p>
      </div>

      {books.length === 0 ? (
        <Empty />
      ) : (
        <div className="space-y-2">
          {books.map((b) => (
            <BookCard
              key={b.party.id}
              data={outsideCardData(b)}
              selected={selectedId === b.party.id}
              onSelect={() => setSelectedId(b.party.id)}
              onOpen={() =>
                navigate({
                  to: "/workshop/outside-worker-book/$partyId",
                  params: { partyId: b.party.id },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="rounded-md border border-dashed border-border bg-card/40 p-12 text-center">
      <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
      <h3 className="mt-4 font-serif text-xl text-gold">No outside worker books yet</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        A book opens once gold is sent to an outside worker in the Outside Work module.
      </p>
    </div>
  );
}
